import { useState } from "react";
import { useCreate, useTranslate } from "@refinedev/core";
import { Button, Field, Modal } from "@fawkes/ui";

// Relaties create — the "Nieuwe relatie" dialog (Jmix RelatieService.savePersoon/saveOrganisatie).
// A person/organisation toggle drives which identity fields show; the address + contact blocks are
// shared. POSTs to the relative `relaties` resource (dataProvider prepends /api) via useCreate, gated
// server-side by ROLE_RELATIE_EDIT. On success it hands the new id back so the host navigates to the
// detail. Roles/colour-code tags are set afterwards via the editable Persoonsgegevens tile.

type RelatieType = "PERSOON" | "ORGANISATIE";

interface CreateForm {
  type: RelatieType;
  aanhef: string;
  voorletters: string;
  tussenvoegsel: string;
  achternaam: string;
  bedrijfsnaam: string;
  kvkNummer: string;
  btwNummer: string;
  straat: string;
  huisnummer: string;
  huisnummerToevoeging: string;
  postcode: string;
  woonplaats: string;
  land: string;
  email: string;
  telefoon1: string;
  telefoon2: string;
  contactAanhef: string;
  contactVoorletters: string;
  contactTussenvoegsel: string;
  contactAchternaam: string;
}

const EMPTY: CreateForm = {
  type: "PERSOON", aanhef: "", voorletters: "", tussenvoegsel: "", achternaam: "",
  bedrijfsnaam: "", kvkNummer: "", btwNummer: "",
  straat: "", huisnummer: "", huisnummerToevoeging: "", postcode: "", woonplaats: "", land: "",
  email: "", telefoon1: "", telefoon2: "",
  contactAanhef: "", contactVoorletters: "", contactTussenvoegsel: "", contactAchternaam: "",
};

type CreateNotice = "required" | "requiredBedrijf" | "forbidden" | "error" | null;

export function RelatieCreateDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const t = useTranslate();
  const [form, setForm] = useState<CreateForm>(EMPTY);
  const [notice, setNotice] = useState<CreateNotice>(null);
  const { mutate, isLoading: saving } = useCreate();

  const set = (k: keyof CreateForm, v: string) => { setForm((f) => ({ ...f, [k]: v })); setNotice(null); };
  const isOrg = form.type === "ORGANISATIE";

  const save = () => {
    if (isOrg && !form.bedrijfsnaam.trim()) { setNotice("requiredBedrijf"); return; }
    if (!isOrg && !form.achternaam.trim()) { setNotice("required"); return; }
    mutate(
      { resource: "relaties", values: form },
      {
        onSuccess: (res) => {
          const id = (res?.data as { id?: number } | undefined)?.id;
          if (id != null) onCreated(String(id));
        },
        onError: (err) => {
          const code = (err as { statusCode?: number }).statusCode;
          setNotice(code === 403 ? "forbidden" : "error");
        },
      },
    );
  };

  const noticeText =
    notice === "required" ? t("relatieDetailView.edit.required")
    : notice === "requiredBedrijf" ? t("relatieDetailView.edit.requiredBedrijf")
    : notice === "forbidden" ? t("relatieDetailView.edit.forbidden")
    : notice === "error" ? t("relatieDetailView.edit.error")
    : null;

  return (
    <Modal
      open
      title={t("relatieListView.create.title")}
      onClose={onClose}
      footer={
        <>
          {noticeText && <span className="fawkes-error" role="status">{noticeText}</span>}
          <Button variant="secondary" onClick={onClose} disabled={saving}>{t("relatieDetailView.edit.cancel")}</Button>
          <Button variant="primary" onClick={save} disabled={saving}>{t("relatieDetailView.edit.save")}</Button>
        </>
      }
    >
      <div className="fawkes-relatie-tile">
        <div className="fawkes-relatie-create__type">
          <label className="fawkes-relatie-create__type-opt">
            <input type="radio" name="relatie-type" checked={!isOrg} onChange={() => set("type", "PERSOON")} />
            <span>{t("relatieListView.type.persoon")}</span>
          </label>
          <label className="fawkes-relatie-create__type-opt">
            <input type="radio" name="relatie-type" checked={isOrg} onChange={() => set("type", "ORGANISATIE")} />
            <span>{t("relatieListView.type.organisatie")}</span>
          </label>
        </div>

        <div className="fawkes-form-grid">
          {isOrg ? (
            <>
              <Field label={t("relatieDetailView.field.bedrijfsnaam")} value={form.bedrijfsnaam} onChange={(e) => set("bedrijfsnaam", e.target.value)} required />
              <Field label={t("relatieDetailView.field.kvk")} value={form.kvkNummer} onChange={(e) => set("kvkNummer", e.target.value)} />
              <Field label={t("relatieDetailView.field.btw")} value={form.btwNummer} onChange={(e) => set("btwNummer", e.target.value)} />
            </>
          ) : (
            <>
              <Field label={t("relatieDetailView.field.aanhef")} value={form.aanhef} onChange={(e) => set("aanhef", e.target.value)} />
              <Field label={t("relatieDetailView.field.voorletters")} value={form.voorletters} onChange={(e) => set("voorletters", e.target.value)} />
              <Field label={t("relatieDetailView.field.tussenvoegsel")} value={form.tussenvoegsel} onChange={(e) => set("tussenvoegsel", e.target.value)} />
              <Field label={t("relatieDetailView.field.achternaam")} value={form.achternaam} onChange={(e) => set("achternaam", e.target.value)} required />
            </>
          )}
        </div>

        <h5 className="fawkes-relatie-tile__subhead">{t("relatieDetailView.section.contact")}</h5>
        <div className="fawkes-form-grid">
          <Field label={t("relatieDetailView.field.email")} type="email" invalidEmailMessage={t("validatie.emailadres")}
            value={form.email} onChange={(e) => set("email", e.target.value)} />
          <Field label={t("relatieDetailView.field.telefoon1")} value={form.telefoon1} onChange={(e) => set("telefoon1", e.target.value)} />
          <Field label={t("relatieDetailView.field.telefoon2")} value={form.telefoon2} onChange={(e) => set("telefoon2", e.target.value)} />
        </div>

        <h5 className="fawkes-relatie-tile__subhead">{t("relatieDetailView.section.adres")}</h5>
        <div className="fawkes-form-grid">
          <Field label={t("relatieDetailView.field.straat")} value={form.straat} onChange={(e) => set("straat", e.target.value)} />
          <Field label={t("relatieDetailView.field.huisnummer")} value={form.huisnummer} onChange={(e) => set("huisnummer", e.target.value)} />
          <Field label={t("relatieDetailView.field.toevoeging")} value={form.huisnummerToevoeging} onChange={(e) => set("huisnummerToevoeging", e.target.value)} />
          <Field label={t("relatieDetailView.field.postcode")} value={form.postcode} onChange={(e) => set("postcode", e.target.value)} />
          <Field label={t("relatieDetailView.field.woonplaats")} value={form.woonplaats} onChange={(e) => set("woonplaats", e.target.value)} />
          <Field label={t("relatieDetailView.field.land")} value={form.land} onChange={(e) => set("land", e.target.value)} />
        </div>

        {isOrg && (
          <>
            <h5 className="fawkes-relatie-tile__subhead">{t("relatieListView.create.contactpersoon")}</h5>
            <div className="fawkes-form-grid">
              <Field label={t("relatieDetailView.field.aanhef")} value={form.contactAanhef} onChange={(e) => set("contactAanhef", e.target.value)} />
              <Field label={t("relatieDetailView.field.voorletters")} value={form.contactVoorletters} onChange={(e) => set("contactVoorletters", e.target.value)} />
              <Field label={t("relatieDetailView.field.tussenvoegsel")} value={form.contactTussenvoegsel} onChange={(e) => set("contactTussenvoegsel", e.target.value)} />
              <Field label={t("relatieDetailView.field.achternaam")} value={form.contactAchternaam} onChange={(e) => set("contactAchternaam", e.target.value)} />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
